import React, { useEffect, useState } from 'react';
import {
  message,
  Form,
  Input,
  Layout,
  Typography,
  Row,
  Col,
  Select,
} from 'antd';
import { CaretLeftOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { get } from 'lodash';

import AdminApi from 'services/admin';

import { PageSpinner } from 'components/page-spinner';

import PdButton from 'components/pd-button';

const { Content } = Layout;
const { Option } = Select;

const { Title } = Typography;

export function AdminCreateEdit() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [value, setValue] = useState({});
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const location = useLocation();

  const currentPath = location.pathname;
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  async function handleSubmit(values) {
    try {
      setLoadingButton(true);

      if (currentPath === '/profile') {
        await AdminApi.updateAdmin(value.id, { ...value, ...values });
        message.success('Successfully updated profile');
        navigate({
          pathname: '/',
        });
      } else {
        if (id) {
          await AdminApi.updateAdmin(id, { ...value, ...values });
          message.success('Successfully updated admin');
          navigate({
            pathname: '/admins',
          });
        } else {
          await AdminApi.createAdmin(values);
          message.success('Successfully created new admin');
          navigate({
            pathname: '/admins',
          });
        }
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingButton(false);
    }
  }

  function compareToFirstPassword(rule, value) {
    if (value && value !== form.getFieldValue('password')) {
      return Promise.reject('Two passwords that you enter is inconsistent!');
    }
    return Promise.resolve();
  }

  async function getCurrentAdmin() {
    try {
      setLoadingPage(true);
      const { data } = await AdminApi.getAdminMe();
      const currentAdmin = data.data;
      setValue(currentAdmin);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingPage(false);
    }
  }

  async function getRoleList() {
    try {
      const { data } = await AdminApi.getRoles();
      const roleData = data.data;
      setRoleList(roleData);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    }
  }

  async function getAdmin(id) {
    try {
      const { data } = await AdminApi.getAdmin(id);
      if (!data) {
        navigate({
          pathname: '/admins',
        });
      } else {
        const selectedAdmin = data.data;
        setValue(selectedAdmin);
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    }
  }
  useEffect(() => {
    if (currentPath === '/profile') {
      getCurrentAdmin();
    } else {
      getRoleList();
      if (id) {
        getAdmin(id);
      } else {
        setValue([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, currentPath, id]);

  useEffect(() => {
    if (value && (value.first_name || value.email)) {
      form.setFieldsValue({
        first_name: value.first_name,
        last_name: value.last_name,
        email: value.email,
        role_id: value?.role?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-admin main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">
            <Title>
              <Link to={currentPath === '/profile' ? '/' : '/admins'}>
                <CaretLeftOutlined />
              </Link>
              {currentPath === '/admins/create' ? 'Create' : 'Edit'} Admin
            </Title>
          </div>
          <Content className="pageContent">
            <Form
              form={form}
              onFinish={handleSubmit}
              layout="vertical"
              className="pd-admin-create-form"
              initialValues={{
                first_name: value?.first_name,
                last_name: value?.last_name,
                email: value?.email,
                role_id: value?.role?.id,
              }}>
              <Form.Item
                label="First name"
                name="first_name"
                rules={[
                  {
                    required: true,
                    message: 'Please input your first name!',
                  },
                ]}>
                <Input placeholder="Enter first name" value={value?.first_name} />
              </Form.Item>

              <Form.Item label="Last name" name="last_name">
                <Input placeholder="Enter last name" value={value?.last_name} />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                ]}>
                <Input placeholder="Enter email" autoComplete="false" value={value?.email} />
              </Form.Item>

              {currentPath !== '/profile' && (
                <Form.Item
                  label="Role"
                  name="role_id"
                  rules={[
                    { required: true, message: 'Please input your Role!' },
                  ]}>
                  <Select className="inputForm" placeholder="Select role">
                    {roleList.map((role, i) => (
                      <Option key={i} value={role.id}>
                        {role.title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {currentPath === '/profile' && (
                <Form.Item label="Password">
                  <Row gutter={20}>
                    <Col span={19}>
                      <Input disabled />
                    </Col>
                    <Col span={5}>
                      <Link to={'/profile/change-password'}>
                        <PdButton type="primary" full outline>
                          Change Password
                        </PdButton>
                      </Link>
                    </Col>
                  </Row>
                </Form.Item>
              )}

              {currentPath === '/admins/edit' && (
                <Form.Item label="Change Password" name="password">
                  <Input type="password" placeholder="New Password" />
                </Form.Item>
              )}

              {currentPath !== '/profile' && !id && (
                <>
                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: 'Please input your Password!',
                      },
                    ]}>
                    <Input
                      type="password"
                      placeholder="Password"
                      autoComplete="false"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Password Confirmation"
                    name="password_confirmation"
                    rules={[
                      {
                        required: true,
                        message: 'Please input your Password!',
                      },
                      {
                        validator: compareToFirstPassword,
                      },
                    ]}>
                    <Input
                      type="password"
                      placeholder="Password Confirmation"
                    />
                  </Form.Item>
                </>
              )}

              <Form.Item>
                <PdButton
                  type="primary"
                  full
                  htmlType="submit"
                  loading={loadingButton}>
                  Submit
                </PdButton>
              </Form.Item>
            </Form>
          </Content>
        </div>
      </div>
    </Layout>
  );
}
